import { reactive } from "vue";
import { onAuthStateChanged, signOut as firebaseSignOut } from "firebase/auth";
import { auth } from "../firebase-config.js";
import {
  createNewCustomer,
  signUserInWithEmailAndPassword,
  sendResetEmail,
  mapAuthError,
  useBusinessSessionPersistence,
} from "../firebase-authentication.js";
import { identify, resetAnalytics, track } from "../analytics.js";
import { getEmployersForUser } from "../firebase-business.js";
import { clearForUser } from "./businessContext.js";

// Module-level reactive singleton - matches the codebase's plain-JS pattern
// (see analytics.js / firebase-database.js: module functions, no classes).

export const authStore = reactive({
  user: null,
  authReady: false,
  employerBusinessIds: null,
  employersLoading: false,
});

let initialised = false;
let authReadyResolve = null;
const authReadyPromise = new Promise((resolve) => {
  authReadyResolve = resolve;
});

// Idempotent. Registers the single onAuthStateChanged listener and primes
// `authReady` + `employerBusinessIds` so the router guard can rely on them.
export const initAuthStore = () => {
  if (initialised) return;
  initialised = true;

  onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      authStore.user = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
      };
      if (firebaseUser.email) {
        identify(firebaseUser.email);
      }
      if (!authStore.authReady) {
        authStore.authReady = true;
        if (authReadyResolve) {
          authReadyResolve();
          authReadyResolve = null;
        }
      }
      await refreshEmployers();
    } else {
      const previousEmail = authStore.user?.email || null;
      authStore.user = null;
      authStore.employerBusinessIds = null;
      authStore.employersLoading = false;
      clearForUser(previousEmail);
      resetAnalytics();
      if (!authStore.authReady) {
        authStore.authReady = true;
        if (authReadyResolve) {
          authReadyResolve();
          authReadyResolve = null;
        }
      }
    }
  });
};

// Resolves immediately once Firebase has emitted its first auth state, so
// the router guard never decides on a stale `user` value during a hard refresh.
export const whenAuthReady = () => {
  if (authStore.authReady) return Promise.resolve();
  return authReadyPromise;
};

// Auth-only sign-up. MUST NOT write any RTDB records - business + employee
// records are created later by the Stripe Checkout backend.
export const signUpOwner = async (email, password) => {
  try {
    // Business staff get a persistent (localStorage) session.
    await useBusinessSessionPersistence();
    const credential = await createNewCustomer(email, password);
    return { user: credential.user };
  } catch (error) {
    throw mapAuthError(error);
  }
};

export const signIn = async (email, password) => {
  try {
    // Business staff get a persistent (localStorage) session.
    await useBusinessSessionPersistence();
    const credential = await signUserInWithEmailAndPassword(email, password);
    return { user: credential.user };
  } catch (error) {
    throw mapAuthError(error);
  }
};

export const signOut = async () => {
  const previousEmail = authStore.user?.email || null;
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    track("business_signout_failed", {
      error_message: error?.message || "unknown",
      error_code: error?.code || null,
    });
    throw error;
  } finally {
    resetAnalytics();
    clearForUser(previousEmail);
  }
};

// Silent sign-out for transient customer sessions. Unlike signOut() it emits
// no business_* analytics and never throws - the enrollment flow calls it as
// best-effort cleanup once the customer's auth session is no longer needed.
export const signOutCustomer = async () => {
  const previousEmail = authStore.user?.email || null;
  try {
    await firebaseSignOut(auth);
  } catch {
    /* best-effort cleanup - ignore */
  } finally {
    resetAnalytics();
    clearForUser(previousEmail);
  }
};

export const refreshEmployers = async () => {
  const email = authStore.user?.email;
  if (!email) {
    authStore.employerBusinessIds = null;
    return [];
  }
  authStore.employersLoading = true;
  try {
    const ids = await getEmployersForUser(email);
    authStore.employerBusinessIds = ids;
    return ids;
  } finally {
    authStore.employersLoading = false;
  }
};

export const sendOwnerResetEmail = async (email) => {
  try {
    await sendResetEmail(email);
  } catch (error) {
    throw mapAuthError(error);
  }
};
